// biome-ignore-all lint/suspicious/noArrayIndexKey: the preview list is fully replaced on each parsed plan (never reordered or mutated mid-render) — same rationale as ImportPage / DeliveryAddressImport (Task #32).
// =============================================================================
// SchoolsImport — ACARA "Australian Schools List" CSV import (Manage → Schools)
// =============================================================================
// Self-contained section rendered at the bottom of SchoolsAdmin: upload the ACARA
// export → preview (how many are new / already present / duplicate / invalid) →
// TICK which of the new schools to import → Import. All decision logic is the PURE
// planner in src/services/schoolsImport.ts (unit-tested; idempotent re-runs); this
// component only does I/O: read file, load the ACARA dedup baseline, insert, audit.
// =============================================================================
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { createSchools, getExistingAcaraIds } from "../../data/schools";
import { parseSchoolsCsv, planSchoolsImport } from "../../services/schoolsImport";
import { logAudit } from "../../utils/auditLog";
import { GLYPH } from "../../utils/glyphs";
import { logger } from "../../utils/logger";
import { Btn } from "../../utils/ui.jsx";

// How many new schools to render individually (with a checkbox). Beyond this the
// remainder are still imported when selected — kept off-screen for performance
// (the very first bulk load can be ~10,000 rows). Incremental imports are small,
// so this cap is only ever hit on the initial load.
const DISPLAY_CAP = 1000;
const INSERT_CHUNK = 200;

export function SchoolsImport({ onImported, onClose }) {
  const { user } = useSession();
  const [step, setStep] = React.useState("upload"); // upload | preview | importing | done
  const [err, setErr] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [selected, setSelected] = React.useState(() => new Set());
  const [result, setResult] = React.useState(null);

  function reset() {
    setStep("upload");
    setErr("");
    setPlan(null);
    setSelected(new Set());
    setResult(null);
  }

  function handleFile(e) {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async function (ev) {
      const { rows, error } = parseSchoolsCsv(String(ev.target.result));
      if (error) {
        setErr(error);
        return;
      }
      const acaraIds = rows.map((r) => r.acara_id).filter(Boolean);
      const existing = await getExistingAcaraIds(acaraIds);
      if (existing === null) {
        setErr("Couldn't check which schools already exist — try again.");
        return;
      }
      const p = planSchoolsImport(rows, existing);
      setPlan(p);
      // Default: every new school ticked.
      setSelected(new Set(p.newSchools.map((s) => s.acara_id)));
      setStep("preview");
    };
    reader.readAsText(file);
  }

  function toggle(acaraId) {
    setSelected(function (prev) {
      const next = new Set(prev);
      if (next.has(acaraId)) next.delete(acaraId);
      else next.add(acaraId);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(plan.newSchools.map((s) => s.acara_id)));
  }
  function selectNone() {
    setSelected(new Set());
  }

  async function runImport() {
    if (!plan) return;
    const chosen = plan.newSchools.filter((s) => selected.has(s.acara_id));
    if (chosen.length === 0) return;
    setStep("importing");
    const inserts = chosen.map((s) => ({
      acara_id: s.acara_id,
      name: s.name,
      suburb: s.suburb,
      state: s.state,
      postcode: s.postcode,
      school_type: s.school_type,
      school_sl: false,
      deactivated: false,
    }));
    let created = 0;
    let failed = 0;
    for (let i = 0; i < inserts.length; i += INSERT_CHUNK) {
      const chunk = inserts.slice(i, i + INSERT_CHUNK);
      const res = await createSchools(chunk);
      if (res.error) {
        failed += chunk.length;
        logger.warn("Schools import chunk failed:", res.error.message);
      } else {
        created += chunk.length;
      }
    }
    logAudit("schools_imported", {
      created: created,
      failed: failed,
      already_present: plan.alreadyPresent,
      invalid: plan.invalidRows,
      by: user?.email || "",
    });
    setResult({ created: created, failed: failed });
    setStep("done");
    if (created > 0 && typeof onImported === "function") onImported();
  }

  const shownNew = plan ? plan.newSchools.slice(0, DISPLAY_CAP) : [];
  const hiddenNew = plan ? plan.newSchools.length - shownNew.length : 0;

  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "12px 18px" }}>
      <div className="flex justify-between items-start mb-1">
        <div className="text-[16px] font-bold text-zephyr-teal-dark">Import Schools (ACARA)</div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="bg-transparent border-0 text-[20px] text-zephyr-muted cursor-pointer leading-none"
          >
            ×
          </button>
        )}
      </div>
      <div className="text-[13px] text-zephyr-muted mb-4">
        Upload the ACARA <b>"Australian Schools List"</b> export. Schools already in the portal
        (matched on ACARA ID) are skipped; tick which of the new schools to import. Safe to re-run.
      </div>

      <div
        className="text-[13px] text-zephyr-text mb-4 rounded-[8px] border border-zephyr-border p-3"
        style={{ background: "var(--color-zephyr-teal-light)" }}
      >
        <div className="font-bold mb-1 text-zephyr-teal-dark">How to get the file</div>
        <ol className="list-decimal ml-4 flex flex-col gap-1">
          <li>
            Download the list from{" "}
            <a
              href="https://asl.acara.edu.au/School-Search"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-zephyr-teal-dark font-semibold"
            >
              asl.acara.edu.au/School-Search
            </a>
            .
          </li>
          <li>
            Leave the selection as-is and click the <b>EXCEL</b> button — this downloads a{" "}
            <b>.xlsx</b> file.
          </li>
          <li>
            Open that file in Excel and <b>Save As</b> a <b>.csv</b> file (remember where you save
            it).
          </li>
          <li>
            Then continue with this utility below — you'll only be adding the new schools (the
            "extras").
          </li>
        </ol>
      </div>

      {step === "upload" && (
        <div>
          <label
            htmlFor="schools-import-file"
            className="cursor-pointer font-bold underline text-zephyr-teal-dark text-[13px]"
          >
            📄 Choose the ACARA Schools CSV…
          </label>
          <input
            id="schools-import-file"
            type="file"
            accept=".csv,.txt"
            aria-label="ACARA schools CSV file"
            onChange={handleFile}
            className="hidden"
          />
          {err && <div className="text-zephyr-red mt-3 text-[13px]">{err}</div>}
        </div>
      )}

      {step === "preview" && plan && (
        <div>
          <div className="flex gap-6 text-[13px] flex-wrap mb-3">
            <span>
              <strong style={{ color: "#2E7D32" }}>{plan.newSchools.length}</strong> new
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.alreadyPresent}</strong> already in the
              portal (skipped)
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.duplicateInFile}</strong> in-file
              duplicates
            </span>
            <span>
              <strong style={{ color: plan.invalidRows ? "#C62828" : "#607D79" }}>
                {plan.invalidRows}
              </strong>{" "}
              invalid rows
            </span>
          </div>

          {plan.newSchools.length > 0 ? (
            <div>
              <div className="flex gap-3 items-center mb-2 text-[13px]">
                <span className="font-semibold">
                  {`${selected.size} of ${plan.newSchools.length} selected`}
                </span>
                <button
                  type="button"
                  onClick={selectAll}
                  className="underline text-zephyr-teal-dark bg-transparent border-0 cursor-pointer text-[13px]"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={selectNone}
                  className="underline text-zephyr-teal-dark bg-transparent border-0 cursor-pointer text-[13px]"
                >
                  Select none
                </button>
              </div>
              <div
                className="border border-zephyr-border rounded-[8px] overflow-y-auto mb-4 text-[12px]"
                style={{ maxHeight: 300 }}
              >
                {shownNew.map(function (s, i) {
                  return (
                    <label
                      key={`${s.acara_id}-${i}`}
                      className="flex items-center gap-2 py-[6px] px-3 cursor-pointer"
                      style={{ borderTop: i > 0 ? "1px solid #EEF3F2" : "none" }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(s.acara_id)}
                        onChange={function () {
                          toggle(s.acara_id);
                        }}
                      />
                      <span className="text-zephyr-muted w-[64px]">{s.acara_id}</span>
                      <span className="flex-1 text-zephyr-text">{s.name}</span>
                      <span className="text-zephyr-muted">
                        {[s.suburb, s.state, s.postcode].filter(Boolean).join(", ")}
                      </span>
                    </label>
                  );
                })}
                {hiddenNew > 0 && (
                  <div className="py-[6px] px-3 text-zephyr-muted">
                    … and {hiddenNew} more new school{hiddenNew === 1 ? "" : "s"} not shown — use
                    Select all to include them.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-[13px] text-zephyr-muted mb-4">
              No new schools to import — everything in this file is already in the portal.
            </div>
          )}

          <div className="flex gap-[10px]">
            <Btn variant="secondary" onClick={reset}>
              Cancel
            </Btn>
            <Btn onClick={runImport} disabled={selected.size === 0}>
              Import {selected.size} school{selected.size === 1 ? "" : "s"}
            </Btn>
          </div>
        </div>
      )}

      {step === "importing" && (
        <div className="text-[13px] text-zephyr-muted">
          Importing… please don't close this page.
        </div>
      )}

      {step === "done" && result && (
        <div>
          <div className="text-[13px] mb-3">
            <span style={{ color: "#2E7D32", fontWeight: 700 }}>
              {result.created} school{result.created === 1 ? "" : "s"} imported {GLYPH.check}
            </span>
            {result.failed > 0 && (
              <span className="ml-3 text-zephyr-red font-semibold">
                {result.failed} failed — check the console / re-run (already-imported rows are
                skipped).
              </span>
            )}
          </div>
          <div className="flex gap-[10px]">
            <Btn variant="secondary" onClick={reset}>
              Import another file
            </Btn>
            {onClose && <Btn onClick={onClose}>Done</Btn>}
          </div>
        </div>
      )}
    </div>
  );
}
